/**
 * OccurraMark — the Occurra logo as independently animatable layers.
 *
 *   background  the gradient disc. Never transforms: it is the lens body, and
 *               spinning a radial gradient only makes it shimmer.
 *   centre      the ring the mark is built around.
 *   brackets    ONE arc, drawn three times at 120° apart. The mark's
 *               three-fold symmetry is literal here rather than transcribed,
 *               so the three stay identical by construction.
 *
 * Two controls, both supplied by the caller so the mark stays presentational:
 *
 *   `angle`   rotates the whole foreground about the mark's centre.
 *   `spread`  pushes the brackets away from the centre ring, in viewBox units.
 *             Each bracket is rotated into place FIRST and then translated
 *             along its own local +y, so "away" means radially outward for
 *             each one rather than downward for all three.
 *
 * The spread is a translation, not a scale: scaling would thicken the strokes
 * as they travelled, which reads as the mark growing rather than opening.
 *
 * Geometry comes from the exported parts (app-center.svg / app-bracket.svg);
 * the disc keeps the brand gradient from assets/occurra-logo.svg. That file
 * serves the favicon and the static logo spots — this component is for the
 * places that animate.
 */

import React from 'react';

// The mark's true centre, from app-center.svg. Everything pivots about it.
const CX = 100;
const CY = 100.397;

// One arc; the other two are this rotated 120° and 240°.
const BRACKET = 'M149 147C117.462 165.208 79.7998 163.201 51.019 144.998';
const BRACKET_TURNS_DEG = [0, 120, 240];

const STROKE = 16;

// `transform-box: view-box` puts transform-origin and translate lengths in
// viewBox units, so the mark animates identically at any rendered size.
const PIVOT: React.CSSProperties = {
    transformBox: 'view-box',
    transformOrigin: `${CX}px ${CY}px`,
};

interface OccurraMarkProps extends React.ComponentProps<'svg'> {
    /** Rotates the whole foreground about the mark's centre, in degrees. */
    angle?: number;
    /** Pushes the brackets radially outward, in viewBox units. */
    spread?: number;
    durationMs?: number;
}

export const OccurraMark = ({
    angle = 0,
    spread = 0,
    durationMs = 100,
    className,
    ...rest
}: OccurraMarkProps): React.JSX.Element => {
    const ease = `transform ${durationMs}ms ease-in-out`;
    return (
        <svg
            viewBox="0 0 200 200"
            className={className}
            aria-hidden="true"
            {...rest}
        >
            <defs>
                <radialGradient
                    id="occurra-mark-bg"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(100 72) rotate(90) scale(128)"
                >
                    <stop stopColor="#6ADCFF" />
                    <stop
                        offset="0.408654"
                        stopColor="#38C1F6"
                    />
                    <stop
                        offset="0.663462"
                        stopColor="#32A7DE"
                    />
                    <stop
                        offset="0.836538"
                        stopColor="#0E7AFF"
                    />
                    <stop
                        offset="1"
                        stopColor="#A23EFF"
                    />
                </radialGradient>
            </defs>

            {/* Background — deliberately outside every transform group. */}
            <circle
                cx="100"
                cy="100"
                r="100"
                fill="url(#occurra-mark-bg)"
            />

            <g style={{ ...PIVOT, transform: `rotate(${angle}deg)`, transition: ease }}>
                <circle
                    cx={CX}
                    cy={CY}
                    r="24"
                    fill="none"
                    stroke="white"
                    strokeWidth={STROKE}
                />
                {BRACKET_TURNS_DEG.map((turn) => (
                    <g
                        key={turn}
                        style={{
                            ...PIVOT,
                            // rotate THEN translate: the translation runs along this
                            // bracket's own axis, so all three move outward, not downward.
                            transform: `rotate(${turn}deg) translate(0px, ${spread}px)`,
                            transition: ease,
                        }}
                    >
                        <path
                            d={BRACKET}
                            fill="none"
                            stroke="white"
                            strokeWidth={STROKE}
                            strokeLinecap="round"
                        />
                    </g>
                ))}
            </g>
        </svg>
    );
};
